import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  Zap,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from "lucide-react";

const OptimizedRegistration = () => {
  // Multiple webinar session times
  const webinarSessions = [
    {
      id: 1,
      date: "January 16, 2026",
      time: "7:00 PM CST",
      dateTime: new Date("2026-01-16T19:00:00-06:00"), // January 16, 2026 at 7:00 PM CST (UTC-6)
      display: "Fri, Jan 16th 2026 @ 7:00 PM CST"
    }
  ];

  const [selectedSession, setSelectedSession] = useState(webinarSessions[0]);
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const difference = selectedSession.dateTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setTimeRemaining({ days, hours, minutes, seconds });
      } else {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [selectedSession]);

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    selectedWebinarSession: webinarSessions[0].display,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [emailValid, setEmailValid] = useState(null); // null, true, false

  const showToast = (title, description, variant = "default") => {
    setToast({ title, description, variant });
    setTimeout(() => setToast(null), 5000);
  };

  // Comprehensive email validation
  const validateEmail = (email) => {
    // Remove whitespace
    email = email.trim().toLowerCase();

    // Basic format validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }

    // Check for disposable/temporary email domains
    const disposableDomains = [
      "tempmail.org",
      "10minutemail.com",
      "guerrillamail.com",
      "mailinator.com",
      "throwaway.email",
      "temp-mail.org",
      "maildrop.cc",
      "mohmal.com",
      "sharklasers.com",
      "grr.la",
      "guerrillamailblock.com",
      "pokemail.net",
      "spam4.me",
      "bccto.me",
      "chacuo.net",
      "dispostable.com",
      "fake.com",
      "fakeinbox.com",
      "getnada.com",
      "harakirimail.com",
      "incognitomail.org",
      "mytrashmail.com",
      "no-spam.ws",
      "spamgoes.com",
      "tempmailaddress.com",
      "trashmail.org",
      "yopmail.com",
      "emailondeck.com",
      "mailnesia.com",
    ];

    const domain = email.split("@")[1];
    if (disposableDomains.includes(domain)) {
      return "Please use a permanent email address, not a temporary one";
    }

    // Check for common typos in popular domains
    const commonDomains = {
      "gmial.com": "gmail.com",
      "gmai.com": "gmail.com",
      "gmil.com": "gmail.com",
      "yahooo.com": "yahoo.com",
      "yaho.com": "yahoo.com",
      "hotmial.com": "hotmail.com",
      hotmailcom: "hotmail.com",
      "outlok.com": "outlook.com",
      outlookcom: "outlook.com",
    };

    if (commonDomains[domain]) {
      return `Did you mean ${email.replace(domain, commonDomains[domain])}?`;
    }

    // Check for obviously fake patterns
    const fakePatterns = [
      /test@test\./,
      /fake@fake\./,
      /admin@admin\./,
      /user@user\./,
      /example@example\./,
      /demo@demo\./,
      /@localhost/,
      /@127\.0\.0\.1/,
    ];

    for (let pattern of fakePatterns) {
      if (pattern.test(email)) {
        return "Please enter a real email address";
      }
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email before submission
    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      showToast("Email Validation Error", emailValidationError, "destructive");
      return;
    }

    setIsLoading(true);

    try {
      // Prepare registration data for Zapier
      const registrationData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        selected_webinar_session: formData.selectedWebinarSession,
        registration_date: new Date().toISOString(),
        source_url: window.location.href,
      };

      // Log for debugging
      console.log("📝 Sending Registration Data to Zapier:", registrationData);

      // Send data to Zapier webhook using URL-encoded form (avoids CORS preflight)
      const zapierWebhookUrl = "https://hooks.zapier.com/hooks/catch/25101197/uswf6ar/";
      
      // Convert to URL-encoded form data (this content-type doesn't trigger preflight)
      const formBody = new URLSearchParams();
      Object.keys(registrationData).forEach(key => {
        formBody.append(key, registrationData[key]);
      });

      // Use no-cors mode - Zapier will receive the data but we can't read the response
      // This is the only reliable way to send data to Zapier from browser
      await fetch(zapierWebhookUrl, {
        method: "POST",
        body: formBody,
        mode: "no-cors"
      });

      // With no-cors, we can't verify response, but data is sent successfully
      console.log("✅ Registration data sent to Zapier (no-cors mode)");

      // Store registration email for download tracking
      sessionStorage.setItem("registeredEmail", formData.email);

      // Success message
      showToast(
        "Registration Confirmed! ",
        "Redirecting you to download your free checklist...",
        "success"
      );

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        selectedWebinarSession: webinarSessions[0].display,
      });

      // Redirect to thank you page after successful registration
      setTimeout(() => {
        navigate("/thank-you");
      }, 1500);

    } catch (error) {
      console.error("❌ Registration error:", error);
      
      // Show error to user
      showToast(
        "Registration Error",
        "There was a problem submitting your registration. Please try again.",
        "destructive"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Real-time email validation
    if (name === "email") {
      if (value === "") {
        setEmailError("");
        setEmailValid(null);
      } else {
        const error = validateEmail(value);
        setEmailError(error);
        setEmailValid(error === "");
      }
    }
  };

  return (
    <section
      id="registration"
      className="py-8 sm:py-12 lg:py-16 bg-white w-full overflow-hidden">
      <div className="container mx-auto px-2 sm:px-4 max-w-full">
        <div className="max-w-2xl mx-auto w-full">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3 text-gray-900 leading-tight px-2">
              Reserve Your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                FREE Seat
              </span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-lg mx-auto px-2">
              Join 1,200+ registered home buyers. Get instant access to our
              exclusive 27-Point First-Time Home Buyer's Checklist!
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg sm:rounded-2xl p-3 sm:p-4 lg:p-6 border border-purple-100 mx-2 sm:mx-0">
            {/* Session Dropdown with Countdown */}
            <div className="mb-4 sm:mb-6">
              <div className="relative mb-3">
                <select 
                  value={selectedSession.id}
                  onChange={(e) => {
                    const session = webinarSessions.find(s => s.id === parseInt(e.target.value));
                    setSelectedSession(session);
                    setFormData(prev => ({ ...prev, selectedWebinarSession: session.display }));
                  }}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm sm:text-base text-gray-700 font-medium focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none cursor-pointer"
                  style={{ 
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }}
                >
                  {webinarSessions.map(session => (
                    <option key={session.id} value={session.id}>
                      {session.display}
                    </option>
                  ))}
                </select>
              </div>

              {/* Countdown Display */}
              {timeRemaining.days > 0 && (
                <p className="text-xs sm:text-sm text-gray-600 text-center mb-3">
                  Starts in {timeRemaining.days} day{timeRemaining.days !== 1 ? 's' : ''}
                </p>
              )}

              <div className="grid grid-cols-4 gap-2 sm:gap-3 max-w-md mx-auto">
                <div className="flex flex-col items-center">
                  <div className="bg-indigo-600 rounded-lg w-full py-3 px-2">
                    <div className="text-2xl sm:text-3xl font-bold text-white text-center">
                      {String(timeRemaining.days).padStart(2, '0')}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 uppercase font-medium">DAYS</div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="bg-indigo-600 rounded-lg w-full py-3 px-2">
                    <div className="text-2xl sm:text-3xl font-bold text-white text-center">
                      {String(timeRemaining.hours).padStart(2, '0')}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 uppercase font-medium">HOURS</div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="bg-indigo-600 rounded-lg w-full py-3 px-2">
                    <div className="text-2xl sm:text-3xl font-bold text-white text-center">
                      {String(timeRemaining.minutes).padStart(2, '0')}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 uppercase font-medium">MINS</div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="bg-indigo-600 rounded-lg w-full py-3 px-2">
                    <div className="text-2xl sm:text-3xl font-bold text-white text-center">
                      {String(timeRemaining.seconds).padStart(2, '0')}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 uppercase font-medium">SECS</div>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* First Name */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 mr-2 text-purple-600" />
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                  placeholder="Enter your first name"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 mr-2 text-purple-600" />
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                  placeholder="Enter your last name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Mail
                    className={`w-4 h-4 mr-2 transition-colors ${
                      emailValid === true
                        ? "text-green-600"
                        : emailValid === false
                        ? "text-red-600"
                        : "text-purple-600"
                    }`}
                  />
                  Email Address *
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 sm:px-4 py-3 border-2 rounded-lg focus:ring-2 focus:border-transparent text-base transition-all duration-300 pr-10 ${
                      emailValid === true
                        ? "border-green-500 bg-green-50 focus:ring-green-500"
                        : emailValid === false
                        ? "border-red-500 bg-red-50 focus:ring-red-500"
                        : "border-gray-300 bg-white focus:ring-purple-500"
                    }`}
                    placeholder="your.email@company.com"
                  />
                  {/* Validation Icon */}
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {emailValid === true && (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                    {emailValid === false && (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>
                {emailError && (
                  <div className="mt-2 text-sm text-red-600 flex items-center animate-pulse">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {emailError}
                  </div>
                )}
                {emailValid === true && (
                  <div className="mt-2 text-sm text-green-600 flex items-center">
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Perfect! We'll send your webinar access link here
                  </div>
                )}
                {emailValid === null && formData.email === "" && (
                  <div className="mt-1 text-xs text-gray-500">
                    We'll send your webinar access link here
                  </div>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 mr-2 text-purple-600" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || emailValid === false}
                className={`w-full py-3 sm:py-4 px-6 sm:px-8 rounded-lg font-semibold text-base sm:text-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transform transition-all duration-300 disabled:cursor-not-allowed disabled:transform-none ${
                  emailValid === true && !isLoading
                    ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 focus:ring-green-500 hover:-translate-y-0.5 hover:shadow-xl"
                    : emailValid === false
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed opacity-50"
                    : "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 focus:ring-purple-500 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50"
                }`}>
                <div className="flex items-center justify-center">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      <span className="hidden sm:inline">
                        Securing Your Seat...
                      </span>
                      <span className="sm:hidden">Securing...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" />
                      <span className="hidden sm:inline">
                        Reserve My Free Seat + 27-Point Checklist
                      </span>
                      <span className="sm:hidden">Reserve Free Seat</span>
                    </>
                  )}
                </div>
              </button>

              {/* Trust Indicators */}
              <div className="text-center text-xs sm:text-sm text-gray-500 space-y-1">
                <p>🔒 Your information is secure and will not be shared</p>
                <p>✨ Instant access • No spam • Unsubscribe anytime</p>
              </div>
            </form>
          </div>

          {/* Toast Notification */}
          {toast && (
            <div
              className={`fixed top-4 left-4 right-4 sm:top-4 sm:right-4 sm:left-auto sm:max-w-md max-w-full p-4 rounded-lg shadow-lg transition-all duration-500 z-50 border ${
                toast.variant === "destructive"
                  ? "bg-white text-red-700 border-red-300"
                  : "bg-white text-green-700 border-green-300"
              }`}>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  {toast.variant === "destructive" ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm mb-1 text-gray-900">
                    {toast.title}
                  </div>
                  <div className="text-xs leading-relaxed text-gray-600 line-clamp-3">
                    {toast.variant === "destructive"
                      ? toast.description
                      : "Registration confirmed! Check your email for webinar access link and materials."}
                  </div>
                </div>
                <button
                  onClick={() => setToast(null)}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors">
                  <span className="sr-only">Close</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default OptimizedRegistration;
